import React from 'react';

/**
 * Lazy-loads a route component, with one automatic recovery from a stale chunk reference.
 *
 * Vite emits content-hashed chunk filenames, so every deploy publishes a new set and removes the
 * old one. A user who had the app open across a deploy is holding an `index.html` that names chunks
 * the server no longer serves. The first navigation to a not-yet-loaded page then rejects — and
 * because that rejection surfaces during render rather than as a fetch the caller can inspect, it
 * reaches the error boundary and the user sees a crash on a perfectly healthy application.
 *
 * A single reload fixes it: it fetches the new `index.html`, and with it the new chunk names. The
 * attempt is recorded in `sessionStorage` and made only once per chunk, so a chunk that is genuinely
 * broken surfaces as an error instead of an infinite reload loop. The record is cleared on success,
 * so a second deploy in the same session still gets its own recovery. If the record cannot be
 * written at all, no reload is attempted — see `recordAttempt`.
 *
 * Recovery is limited to *load* failures. A module that fetched successfully and then threw during
 * evaluation is an ordinary bug, and is rethrown to the error boundary with the user's state intact.
 */
export function lazyPage<T extends React.ComponentType<Record<string, never>>>(
  importer: () => Promise<{ default: T }>,
  chunkId: string,
) {
  const key = `askiam-chunk-reload:${chunkId}`;

  return React.lazy(() =>
    importer()
      .then((module) => {
        clearAttempt(key);
        return module;
      })
      .catch((error: unknown) => {
        // Only a chunk that failed to *load* is a stale-chunk candidate. An exception thrown while
        // evaluating a module that fetched perfectly well is an ordinary bug, and reloading for it
        // would discard the user's client state and then land them on the same error anyway.
        if (!isChunkLoadFailure(error)) throw error;

        // If the attempt cannot be recorded, treat it as already attempted. Reloading without a
        // durable record risks an unbreakable reload loop when the chunk is genuinely missing,
        // which is a far worse outcome than surfacing the error once.
        if (!recordAttempt(key)) throw error;

        window.location.reload();

        // The reload is asynchronous. Return a promise that never settles so React keeps showing
        // the Suspense fallback rather than committing the rejection in the meantime.
        return new Promise<{ default: T }>(() => {});
      }),
  );
}

/**
 * Distinguishes "the chunk never arrived" from "the chunk arrived and threw". Browsers disagree on
 * the wording, and none of them offer a reliable error code, so this matches the known shapes and
 * treats anything unrecognised as a real error — the safe direction, since the cost of a missed
 * recovery is one visible error the user can retry, while the cost of a false positive is silently
 * discarding their work.
 */
function isChunkLoadFailure(error: unknown): boolean {
  const record = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  if (String(record.name ?? '') === 'ChunkLoadError') return true;

  const message = 'message' in record ? String(record.message) : String(error);

  return (
    /failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /importing a module script failed/i.test(message)
    || /loading chunk \S+ failed/i.test(message)
    || /css_chunk_load_failed/i.test(message)
  );
}

/**
 * Records a recovery attempt, returning false if one was already recorded or if session storage is
 * unusable. Storage access throws outright in sandboxed frames and some hardened privacy modes, and
 * a helper whose job is to rescue a failed load must not fail on the way.
 */
function recordAttempt(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
      return false;
    }

    sessionStorage.setItem(key, '1');
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function clearAttempt(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
